// Play view - combined active games and adventures marketplace

import { css, html } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { router } from '../../lib/router.js';
import { platformState } from '../../state/platform-state.js';
import '../shared/confirm-delete.js';

/**
 * Combined play view showing active games and available adventures
 */
class PlayView extends BaseComponent {
  static properties = {
    _games: { type: Array, state: true },
    _adventures: { type: Array, state: true },
    _gamesLoading: { type: Boolean, state: true },
    _adventuresLoading: { type: Boolean, state: true },
    _gamesError: { type: String, state: true },
    _showDeleteModal: { type: Boolean, state: true },
    _gameToDelete: { type: String, state: true },
    _showRestartModal: { type: Boolean, state: true },
    _restartGameId: { type: String, state: true },
    _restartSlug: { type: String, state: true },
    _restartName: { type: String, state: true },
  };

  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      flex: 1;
    }

    .play-layout {
      display: flex;
      flex-direction: column;
      flex: 1;
      gap: 2rem;
    }

    h2 {
      font-family: Georgia, 'Times New Roman', serif;
      color: #212529;
      margin-bottom: 1rem;
    }

    /* Game list */
    .game-list {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .game-item {
      display: flex;
      flex-direction: column;
      gap: 0.375rem;
      padding: 0.625rem 0.75rem;
      border: 1px solid #dee2e6;
      border-radius: 0.375rem;
      background: #fff;
    }

    .game-item-name {
      font-size: 0.875rem;
      font-weight: 500;
    }

    .game-item-actions {
      display: flex;
      gap: 0.375rem;
    }

    .game-item-actions .btn {
      padding: 0.25rem 0.625rem;
      font-size: 0.75rem;
    }

    /* Adventure cards */

    .cover-image {
      height: 180px;
      background-size: cover;
      background-position: center;
    }

    .cover-placeholder {
      height: 180px;
      background: linear-gradient(135deg, #2d3561 0%, #c05c7e 100%);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .cover-placeholder h3 {
      color: white;
      font-weight: bold;
      text-align: center;
      padding: 0 1rem;
      margin: 0;
    }

    .meta {
      display: flex;
      gap: 1rem;
      font-size: 0.8rem;
      color: #6c757d;
      margin-bottom: 0.75rem;
    }

    .meta svg {
      width: 14px;
      height: 14px;
      margin-right: 2px;
      vertical-align: -2px;
    }

    .empty-state {
      text-align: center;
      color: #6c757d;
      padding: 2rem 0;
    }

    .play-layout.mobile {
      padding: 1rem;
      background: #f8f9fa;
    }

    .mobile-subtitle {
      color: #6c757d;
      font-size: 0.9rem;
      margin: 0 0 0.75rem 0;
    }

    .play-layout.mobile .cover-image {
      height: 140px;
      border-radius: 8px 8px 0 0;
    }

    .play-layout.mobile .cover-placeholder {
      height: 140px;
      border-radius: 8px 8px 0 0;
    }

    .play-layout.mobile .card {
      border-radius: 8px;
      overflow: hidden;
    }

    .play-layout.mobile .card-body {
      padding: 0.75rem;
    }

    .play-layout.mobile .card-title {
      font-size: 1rem;
      margin-bottom: 0.25rem;
    }

    .play-layout.mobile .card-text {
      font-size: 0.8rem;
      margin-bottom: 0.5rem;
    }

    .play-layout.mobile .btn {
      padding: 0.5rem 1rem;
      font-size: 0.9rem;
    }

    @media (min-width: 992px) {
      .play-layout {
        flex-direction: row;
        align-items: stretch;
        gap: 2rem;
      }

      .games-sidebar {
        width: 260px;
        flex-shrink: 0;
        order: 1;
        margin: -1.25rem -1.25rem -1.25rem 0;
        padding: 1.25rem 1.25rem 1.25rem 1.5rem;
        border-left: 1px solid #dee2e6;
        background: #f8f9fa;
      }

      .games-sidebar h2 {
        font-size: 1rem;
        margin-bottom: 0.75rem;
      }

      .adventures-main {
        flex: 1;
        min-width: 0;
      }
    }
  `;

  constructor() {
    super();
    /** @type {Array<import('../../types/jsdoc-types.js').Game>} */
    this._games = [];
    /** @type {Array<{id: string, name: string, description: string, capsuleImage: string|null, slug: string, difficulty: string|null, estimatedTime: string}>} */
    this._adventures = [];
    this._gamesLoading = true;
    this._adventuresLoading = true;
    /** @type {string|null} */
    this._gamesError = null;
    this._showDeleteModal = false;
    /** @type {string|null} */
    this._gameToDelete = null;
    this._showRestartModal = false;
    /** @type {string|null} */
    this._restartGameId = null;
    /** @type {string|null} */
    this._restartSlug = null;
    /** @type {string|null} */
    this._restartName = null;
  }

  async connectedCallback() {
    await super.connectedCallback();
    this._loadGames();
    this._loadAdventures();
  }

  async _loadGames() {
    this._gamesLoading = true;
    this._gamesError = null;
    try {
      this._games = /** @type {Array<import('../../types/jsdoc-types.js').Game>} */ (await api.listGames());
    } catch (err) {
      console.error('Failed to load games:', err);
      this._gamesError = 'Failed to load games.';
    } finally {
      this._gamesLoading = false;
    }
  }

  async _loadAdventures() {
    this._adventuresLoading = true;
    try {
      const data =
        /** @type {Array<{id: string, name: string, description: string, capsule_image_url: string, slug: string, level: number, duration: string}>} */ (
          await api.getMarketplaceAdventures()
        );
      this._adventures = data.map((a) => ({
        id: a.id,
        name: a.name,
        description: a.description,
        capsuleImage: api.getImageUrl(a.capsule_image_url),
        slug: a.slug,
        difficulty: a.level ? `Level ${a.level}` : null,
        estimatedTime: a.duration,
      }));
    } catch (err) {
      console.error('Failed to load adventures:', err);
    } finally {
      this._adventuresLoading = false;
    }
  }

  /** @param {string} dateString */
  _formatDate(dateString) {
    return new Date(dateString).toLocaleDateString();
  }

  /** @param {string} gameId */
  _handleResume(gameId) {
    router.navigate(`/game/${gameId}`);
  }

  /** @param {string} gameId */
  _handleDelete(gameId) {
    this._gameToDelete = gameId;
    this._showDeleteModal = true;
  }

  async _confirmDelete() {
    this._showDeleteModal = false;
    if (!this._gameToDelete) return;
    try {
      await api.deleteGame(this._gameToDelete);
      this._gameToDelete = null;
      await this._loadGames();
    } catch (err) {
      console.error('Failed to delete game:', err);
    }
  }

  _cancelDelete() {
    this._showDeleteModal = false;
    this._gameToDelete = null;
  }

  /** @param {string} slug */
  _handleStartAdventure(slug) {
    router.navigate(`/adventures/${slug}/start`);
  }

  /**
   * @param {string} gameId
   * @param {string} slug
   * @param {string} name
   */
  _handleRestart(gameId, slug, name) {
    this._restartGameId = gameId;
    this._restartSlug = slug;
    this._restartName = name;
    this._showRestartModal = true;
  }

  async _confirmRestart() {
    this._showRestartModal = false;
    if (!this._restartGameId || !this._restartSlug) return;
    const slug = this._restartSlug;
    try {
      await api.deleteGame(this._restartGameId);
      this._restartGameId = null;
      this._restartSlug = null;
      this._restartName = null;
      router.navigate(`/adventures/${slug}/start`);
    } catch (err) {
      console.error('Failed to restart game:', err);
    }
  }

  _cancelRestart() {
    this._showRestartModal = false;
    this._restartGameId = null;
    this._restartName = null;
    this._restartSlug = null;
  }

  // TODO: The marketplace adventures API should return the active game ID for each adventure
  // so we don't need to cross-reference games and adventures on the frontend.
  /** @param {string} adventureId */
  _getGameForAdventure(adventureId) {
    return this._games.find((g) => g.adventureId === adventureId);
  }

  _renderGamesSection() {
    if (this._gamesLoading) {
      return html`
        <div class="text-center text-muted py-3">
          <div class="spinner-border spinner-border-sm" role="status"></div>
          <span class="ms-2">Loading games...</span>
        </div>
      `;
    }

    if (this._gamesError) {
      return html`<div class="alert alert-danger">${this._gamesError}</div>`;
    }

    if (this._games.length === 0) {
      return html`<p class="empty-state">No active games. Start an adventure below!</p>`;
    }

    return html`
      <div class="game-list">
        ${this._games.map(
          (game) => html`
            <div class="game-item">
              <span class="game-item-name">${game.adventureName || 'Unnamed Adventure'}</span>
              <div class="game-item-actions">
                ${
                  game.partyWiped
                    ? html`<button class="btn btn-primary btn-sm" @click="${() => this._handleResume(game.id)}">
                      <i class="bi bi-arrow-counterclockwise me-1"></i>Retry
                    </button>`
                    : html`<button class="btn btn-primary btn-sm" @click="${() => this._handleResume(game.id)}">Resume</button>`
                }
                <button class="btn btn-outline-danger btn-sm" @click="${() => this._handleDelete(game.id)}">Delete</button>
              </div>
            </div>
          `,
        )}
      </div>
    `;
  }

  _renderAdventuresSection() {
    if (this._adventuresLoading) {
      return html`
        <div class="text-center text-muted py-3">
          <div class="spinner-border spinner-border-sm" role="status"></div>
          <span class="ms-2">Loading adventures...</span>
        </div>
      `;
    }

    if (this._adventures.length === 0) {
      return html`<p class="empty-state">No adventures available yet.</p>`;
    }

    return html`
      <div class="row row-cols-1 row-cols-md-2 row-cols-xxl-3 g-3">
        ${this._sortedAdventures.map((adventure) => {
          const activeGame = this._getGameForAdventure(adventure.id);
          return html`
            <div class="col">
              <div class="card h-100">
                ${
                  adventure.capsuleImage
                    ? html`
                      <div
                        class="cover-image card-img-top"
                        style="background-image: url('${adventure.capsuleImage}')"
                      ></div>
                    `
                    : html`
                      <div class="cover-placeholder card-img-top">
                        <h3>${adventure.name}</h3>
                      </div>
                    `
                }
                <div class="card-body d-flex flex-column">
                  <h5 class="card-title">${adventure.name}</h5>
                  <p class="card-text text-muted small">${adventure.description}</p>
                  <div class="meta">
                    ${adventure.difficulty ? html`<span>${adventure.difficulty}</span>` : ''}
                    ${adventure.estimatedTime ? html`<span>${adventure.estimatedTime}</span>` : ''}
                  </div>
                  ${
                    activeGame
                      ? activeGame.partyWiped
                        ? html`
                          <div class="mt-auto">
                            <p class="text-muted small mb-2">Your party fell in this adventure.</p>
                            <button
                              class="btn btn-primary w-100"
                              @click="${() => this._handleResume(activeGame.id)}"
                            >
                              <i class="bi bi-arrow-counterclockwise me-1"></i>Retry Adventure
                            </button>
                          </div>
                        `
                        : html`
                          <div class="d-flex gap-2 mt-auto">
                            <button
                              class="btn btn-success flex-grow-1"
                              @click="${() => this._handleResume(activeGame.id)}"
                            >
                              Resume
                            </button>
                            <button
                              class="btn btn-outline-danger"
                              @click="${() => this._handleRestart(activeGame.id, adventure.slug, adventure.name)}"
                            >
                              Restart
                            </button>
                          </div>
                        `
                      : html`
                        <button
                          class="btn btn-primary mt-auto"
                          @click="${() => this._handleStartAdventure(adventure.slug)}"
                        >
                          Start Adventure
                        </button>
                      `
                  }
                </div>
              </div>
            </div>
          `;
        })}
      </div>
    `;
  }

  /** @returns {Array} adventures sorted with active games first on mobile */
  get _sortedAdventures() {
    if (!platformState.isMobile) return this._adventures;
    return [...this._adventures].sort((a, b) => {
      const aGame = this._getGameForAdventure(a.id);
      const bGame = this._getGameForAdventure(b.id);
      if (aGame && !bGame) return -1;
      if (!aGame && bGame) return 1;
      return 0;
    });
  }

  render() {
    const isMobile = platformState.isMobile;

    return html`
      <div class="play-layout ${isMobile ? 'mobile' : ''}">
        ${
          !isMobile && (this._games.length > 0 || this._gamesLoading)
            ? html`
              <aside class="games-sidebar">
                <h2>Active Games</h2>
                ${this._renderGamesSection()}
              </aside>
            `
            : ''
        }

        <div class="adventures-main">
          ${
            isMobile
              ? html`<p class="mobile-subtitle">Play stand-alone adventures outside a campaign</p>`
              : html`<h2>Choose an Adventure</h2>`
          }
          ${this._renderAdventuresSection()}
        </div>
      </div>

      ${
        this._showDeleteModal
          ? html`
            <confirm-delete
              ?show="${true}"
              heading="Delete Game"
              message="Are you sure you want to delete this game? This cannot be undone."
              @confirm="${this._confirmDelete}"
              @cancel="${this._cancelDelete}"
            ></confirm-delete>
          `
          : ''
      }
      ${
        this._showRestartModal
          ? html`
            <confirm-delete
              ?show="${true}"
              heading="Restart ${this._restartName} Adventure"
              message="This will delete your current game and start a new one. You will lose all progress and your character will be reset to before the adventure started. Are you sure?"
              confirm-label="Restart"
              @confirm="${this._confirmRestart}"
              @cancel="${this._cancelRestart}"
            ></confirm-delete>
          `
          : ''
      }
    `;
  }
}

customElements.define('play-view', PlayView);
